import type {
  HandoffLimit,
  HandoffSummary,
  IntegratedPlanGroup,
  IntegratedPlanMetrics,
  IntegratedPlanResult,
} from "./types";

interface ErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    details?: unknown;
    traceId?: string;
  };
}

export class ApiError extends Error {
  public readonly code: string;
  public readonly details: unknown;
  public readonly traceId: string | undefined;
  public readonly status: number;

  public constructor(
    message: string,
    status: number,
    code = "REQUEST_FAILED",
    details: unknown = null,
    traceId: string | undefined = undefined,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.traceId = traceId;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function unwrap(value: unknown): unknown {
  if (isRecord(value) && "data" in value) return value.data;
  return value;
}

async function parseResponseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.length === 0) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

async function requestJson(path: string, init?: RequestInit): Promise<unknown> {
  const response = await fetch(path, {
    ...init,
    headers: {
      Accept: "application/json",
      ...init?.headers,
    },
  });
  const body = await parseResponseBody(response);
  if (!response.ok) {
    const envelope = isRecord(body) ? (body as ErrorEnvelope) : {};
    throw new ApiError(
      envelope.error?.message ?? `Request failed (HTTP ${response.status}).`,
      response.status,
      envelope.error?.code,
      envelope.error?.details,
      envelope.error?.traceId,
    );
  }
  return unwrap(body);
}

function invalidResponse(message: string): never {
  throw new ApiError(message, 502, "INVALID_API_RESPONSE");
}

function requiredRecord(value: unknown, label: string): Record<string, unknown> {
  if (!isRecord(value)) invalidResponse(`The Planner returned an invalid ${label}.`);
  return value;
}

function requiredString(value: unknown, label: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    invalidResponse(`The Planner returned an invalid ${label}.`);
  }
  return value;
}

function requiredNumber(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    invalidResponse(`The Planner returned an invalid ${label}.`);
  }
  return value;
}

function requiredInteger(value: unknown, label: string, minimum = 0): number {
  const parsed = requiredNumber(value, label);
  if (!Number.isSafeInteger(parsed) || parsed < minimum) {
    invalidResponse(`The Planner returned an invalid ${label}.`);
  }
  return parsed;
}

function handoffLimit(value: unknown, label: string): HandoffLimit {
  const record = requiredRecord(value, label);
  const minimum = requiredInteger(record.minimum, `${label} minimum`, 1);
  const maximum = requiredInteger(record.maximum, `${label} maximum`, minimum);
  const defaultValue = requiredInteger(record.default, `${label} default`, minimum);
  if (defaultValue > maximum) invalidResponse(`The Planner returned an invalid ${label} default.`);
  return { minimum, maximum, default: defaultValue };
}

export function parseHandoffSummary(value: unknown): HandoffSummary {
  const record = requiredRecord(value, "handoff summary");
  const schemaVersion = requiredString(record.schemaVersion, "schema version");
  const status = requiredString(record.status, "handoff status");
  if (schemaVersion !== "2.0" || status !== "ready") {
    invalidResponse("The Planner returned an unsupported handoff summary.");
  }
  const limits = requiredRecord(record.limits, "handoff limits");
  if (typeof record.largeHandoff !== "boolean") {
    invalidResponse("The Planner returned an invalid large handoff indicator.");
  }
  return {
    handoffId: requiredString(record.handoffId, "handoff ID"),
    schemaVersion: "2.0",
    status: "ready",
    orderCount: requiredInteger(record.orderCount, "order count", 1),
    uniqueSkuCount: requiredInteger(record.uniqueSkuCount, "unique SKU count"),
    uniqueLocationCount: requiredInteger(record.uniqueLocationCount, "unique location count"),
    createdAt: requiredString(record.createdAt, "created timestamp"),
    expiresAt: requiredString(record.expiresAt, "expiry timestamp"),
    largeHandoff: record.largeHandoff,
    limits: {
      maxSkuPerGroup: handoffLimit(limits.maxSkuPerGroup, "Max Unique SKU / Group limit"),
      maxOrdersPerGroup: handoffLimit(limits.maxOrdersPerGroup, "Max Orders / Group limit"),
    },
  };
}

function integratedMetrics(value: unknown): IntegratedPlanMetrics {
  const record = requiredRecord(value, "plan metrics");
  return {
    totalOrders: requiredInteger(record.totalOrders, "total order count", 1),
    groupCount: requiredInteger(record.groupCount, "group count", 1),
    globalUniqueSkuCount: requiredInteger(record.globalUniqueSkuCount, "global unique SKU count"),
    globalUniqueLocationCount: requiredInteger(
      record.globalUniqueLocationCount,
      "global unique location count",
    ),
    repeatedSkuCount: requiredInteger(record.repeatedSkuCount, "repeated SKU count"),
    repeatedLocationCount: requiredInteger(record.repeatedLocationCount, "repeated location count"),
    totalRouteCost: requiredNumber(record.totalRouteCost, "total route cost"),
    maxGroupRouteCost: requiredNumber(record.maxGroupRouteCost, "maximum group route cost"),
    maxGroupWorkload: requiredNumber(record.maxGroupWorkload, "maximum group workload"),
    workloadImbalance: requiredNumber(record.workloadImbalance, "workload imbalance"),
    largestGroupOrderCount: requiredInteger(
      record.largestGroupOrderCount,
      "largest group order count",
      1,
    ),
    maxGroupUniqueSkuCount: requiredInteger(
      record.maxGroupUniqueSkuCount,
      "maximum group unique SKU count",
      1,
    ),
  };
}

function integratedGroup(value: unknown): IntegratedPlanGroup {
  const record = requiredRecord(value, "plan group");
  if (!Array.isArray(record.orderNumbers)) {
    invalidResponse("The Planner returned invalid group order numbers.");
  }
  const orderNumbers = record.orderNumbers.map((orderNumber) =>
    requiredString(orderNumber, "group order number"),
  );
  const orderCount = requiredInteger(record.orderCount, "group order count", 1);
  if (!Array.isArray(record.pickingJobIds)) {
    invalidResponse("The Planner returned invalid picking job IDs.");
  }
  const pickingJobIds = record.pickingJobIds.map((id) => requiredString(id, "picking job ID"));
  if (pickingJobIds.length !== orderCount) {
    invalidResponse("The Planner returned inconsistent picking job coverage.");
  }
  if (orderNumbers.length !== orderCount) {
    invalidResponse("The Planner returned a group with an inconsistent order count.");
  }
  return {
    groupNumber: requiredInteger(record.groupNumber, "group number", 1),
    totalItems:
      record.totalItems === null || record.totalItems === undefined
        ? null
        : requiredInteger(record.totalItems, "group total items", 1),
    orderCount,
    uniqueSkuCount: requiredInteger(record.uniqueSkuCount, "group unique SKU count"),
    uniqueLocationCount: requiredInteger(record.uniqueLocationCount, "group unique location count"),
    routeCost: requiredNumber(record.routeCost, "group route cost"),
    orderNumbers,
    pickingJobIds,
    orderNumbersText: orderNumbers.join(","),
  };
}

export function parseIntegratedPlan(value: unknown): IntegratedPlanResult {
  const record = requiredRecord(value, "selected plan");
  const selection = requiredRecord(record.selection, "plan selection");
  const timings = requiredRecord(record.timings, "plan timings");
  if (!Array.isArray(record.groups)) invalidResponse("The Planner returned invalid plan groups.");
  const groups = record.groups.map(integratedGroup);
  const metrics = integratedMetrics(record.metrics);
  if (groups.length !== metrics.groupCount) {
    invalidResponse("The Planner returned an inconsistent selected group count.");
  }
  if (new Set(groups.map((group) => group.groupNumber)).size !== groups.length) {
    invalidResponse("The Planner returned duplicate selected group numbers.");
  }
  if (groups.reduce((total, group) => total + group.orderCount, 0) !== metrics.totalOrders) {
    invalidResponse("The Planner returned incomplete selected plan coverage.");
  }
  const orderNumbers = groups.flatMap((group) => group.orderNumbers);
  const pickingJobIds = groups.flatMap((group) => group.pickingJobIds);
  if (new Set(pickingJobIds).size !== pickingJobIds.length) {
    invalidResponse("The Planner returned duplicate selected picking job IDs.");
  }
  if (new Set(orderNumbers).size !== orderNumbers.length) {
    invalidResponse("The Planner returned duplicate selected order numbers.");
  }
  return {
    handoffId: requiredString(record.handoffId, "handoff ID"),
    generatedAt: requiredString(record.generatedAt, "generation timestamp"),
    plannerVersion: requiredString(record.plannerVersion, "planner version"),
    routeAdapterVersion: requiredString(record.routeAdapterVersion, "route adapter version"),
    selection: {
      reasonCode: requiredString(selection.reasonCode, "selection reason code"),
      explanation: requiredString(selection.explanation, "selection explanation"),
    },
    metrics,
    groups,
    timings: {
      adaptMs: requiredNumber(timings.adaptMs, "adapt time"),
      normalizeMs: requiredNumber(timings.normalizeMs, "normalization time"),
      planMs: requiredNumber(timings.planMs, "planning time"),
      validateMs: requiredNumber(timings.validateMs, "validation time"),
      totalMs: requiredNumber(timings.totalMs, "total planning time"),
    },
    bulkPickBaseUrl: requiredString(record.bulkPickBaseUrl, "Bulk Pick URL"),
  };
}

export async function getHandoff(handoffId: string, signal?: AbortSignal): Promise<HandoffSummary> {
  const init: RequestInit = { credentials: "include" };
  if (signal !== undefined) init.signal = signal;
  return parseHandoffSummary(
    await requestJson(`/api/v2/handoffs/${encodeURIComponent(handoffId)}`, init),
  );
}

export async function createIntegratedPlan(
  handoffId: string,
  maxSkuPerGroup: number,
  maxOrdersPerGroup: number,
  signal?: AbortSignal,
  maxItemsPerGroup?: number,
): Promise<IntegratedPlanResult> {
  const init: RequestInit = {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ maxSkuPerGroup, maxOrdersPerGroup, maxItemsPerGroup }),
  };
  if (signal !== undefined) init.signal = signal;
  return parseIntegratedPlan(
    await requestJson(`/api/v2/handoffs/${encodeURIComponent(handoffId)}/plan`, init),
  );
}

export function errorDetails(error: unknown): readonly string[] {
  if (!(error instanceof ApiError) || !isRecord(error.details)) return [];
  const details = error.details.errors;
  if (!Array.isArray(details)) return [];
  return details.slice(0, 100).flatMap((item) => {
    if (typeof item === "string") return [item];
    if (!isRecord(item)) return [];
    const rowNumber =
      typeof item.rowNumber === "number"
        ? item.rowNumber
        : typeof item.sourceRowNumber === "number"
          ? item.sourceRowNumber
          : null;
    const row =
      typeof item.orderNumber === "string"
        ? `Order ${item.orderNumber}: `
        : rowNumber === null
          ? ""
          : `Row ${rowNumber}: `;
    return typeof item.message === "string" ? [`${row}${item.message}`] : [];
  });
}

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "An unknown error occurred.";
}
